<template>
  <div ref="layout" class="tree-list-layout">
    <div v-if="headerPanelEnable" ref="headerPanel" class="header-panel">
      <slot name="headerPanel"></slot>
    </div>
    <div class="content-panel" :style="style.contentPanelStyle">
      <div
        v-if="leftPanelEnable"
        ref="leftPanel"
        class="left-panel drag-left"
        :style="style.leftPanelStyle"
      >
        <div class="drag-left-layout" style="position: relative">
          <div class="draggable-panel-position-wrap">
            <slot name="leftPanel"></slot>
          </div>
        </div>
        <div class="drag-layout-handler">
          <div class="switch" @click="leftPanelVisible = !leftPanelVisible">
            <svg
              :class="leftPanelVisible ? 'on' : 'off'"
              width="7px"
              height="14px"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polygon
                points="0,0 6,6 0,12"
                style="fill: #505363; stroke: #505363; stroke-width: 1"
              />
            </svg>
          </div>
        </div>
      </div>
      <div ref="rightPanel" class="right-panel drag-right">
        <div ref="rightPanelWrap" class="draggable-panel-position-wrap">
          <div
            v-if="rightTopPanelEnable"
            ref="rightTopPanel"
            class="right-top-panel"
            @click.native:="rightTopPanelChildEventCatcher"
          >
            <slot name="rightTopPanel"></slot>
          </div>
          <div
            ref="rightMiddlePanel"
            class="right-middle-panel"
            :style="`height:${style.rightMiddlePanelDynamicHeight}px`"
          >
            <slot name="rightMiddlePanel"></slot>
          </div>
          <div
            v-if="rightTopPanelEnable"
            ref="rightTopPanelPlaceholder"
            class="right-top-panel-placeholder"
            :style="`height:${style.rightTopPanelPlaceholderDynamicHeight}px`"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function getOffset(el) {
  const box = el.getBoundingClientRect();

  return {
    top: box.top + window.pageYOffset - document.documentElement.clientTop,
    left: box.left + window.pageXOffset - document.documentElement.clientLeft
  };
}

function getHeight(el) {
  const styles = window.getComputedStyle(el);
  const height = el.offsetHeight;
  const borderTopWidth = parseFloat(styles.borderTopWidth);
  const borderBottomWidth = parseFloat(styles.borderBottomWidth);
  const paddingTop = parseFloat(styles.paddingTop);
  const paddingBottom = parseFloat(styles.paddingBottom);
  return height - borderBottomWidth - borderTopWidth - paddingTop - paddingBottom;
}

function getWidth(el) {
  const styles = window.getComputedStyle(el);
  const width = el.offsetWidth;
  const borderTopWidth = parseFloat(styles.borderTopWidth);
  const borderBottomWidth = parseFloat(styles.borderBottomWidth);
  const paddingTop = parseFloat(styles.paddingTop);
  const paddingBottom = parseFloat(styles.paddingBottom);
  return width - borderBottomWidth - borderTopWidth - paddingTop - paddingBottom;
}

// Private panel size cache
const panelSize = {
  layoutDefaultHeight: 0,
  headerPanelHeight: 0,
  contentPanelClientHeight: 0,
  rightPanelDefaultHeight: 0,
  rightTopPanelDefaultHeight: 0,
  rightMiddlePanelDefaultHeight: 0,
  rightPanelDom: undefined
};
export default {
  name: "TreeListLayout",
  props: {
    headerPanelEnable: {
      type: Boolean,
      default: false
    },
    leftPanelEnable: {
      type: Boolean,
      default: false
    },
    // Left panel draggable max width
    leftPanelMaxWidth: {
      type: Number,
      default: 360
    },
    // Left panel draggable min width
    leftPanelMinWidth: {
      type: Number,
      default: 244
    },
    rightTopPanelEnable: {
      type: Boolean,
      default: false
    },
    // Animation behavior for heigh proformence
    speedMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      leftPanelVisible: true,
      style: {
        contentPanelStyle: "",
        rightTopPanelPlaceholderDynamicHeight: 0,
        rightMiddlePanelDynamicHeight: 0,
        leftPanelStyle: ""
      }
    };
  },
  watch: {
    leftPanelVisible(val) {
      if (val) {
        this.$refs.leftPanel.style.marginLeft = "0";

        if (this.speedMode) {
          this.$refs.leftPanel.style.height = "100%";
          this.$refs.leftPanel.style.position = "absolute";
          this.$refs.leftPanel.style.zIndex = "10";
          const timer = window.setInterval(
            () => {
              if (this.$refs.leftPanel.style.marginLeft === "0px") {
                this.$refs.leftPanel.style.height = "";
                this.$refs.leftPanel.style.position = "";
                this.$refs.leftPanel.style.zIndex = "";
                clearInterval(timer);
              }
            },
            500
          );
        }
      } else {
        const newMarginLeft = `-${this.$refs.leftPanel.clientWidth}px`;
        this.$refs.leftPanel.style.marginLeft = `-${this.$refs.leftPanel.clientWidth}px`;

        if (this.speedMode) {
          this.$refs.rightPanelWrap.style.width = `${this.$refs.rightPanelWrap.offsetWidth}px`;
          const timer = window.setInterval(
            () => {
              if (this.$refs.leftPanel.style.marginLeft === newMarginLeft) {
                this.$refs.rightPanelWrap.style.width = "";
                clearInterval(timer);
              }
            },
            500
          );
        }
      }
    }
  },
  mounted() {
    this.layoutPanelSizeInit();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleDragLeft);
    window.removeEventListener("resize", this.onScrollResetRightMiddlePanelHeight);
  },
  methods: {
    layoutPanelSizeInit() {
      panelSize.layoutDefaultHeight = this.$refs.layout.offsetHeight;

      if (this.headerPanelEnable) {
        panelSize.headerPanelHeight = this.$refs.headerPanel.offsetHeight;
        panelSize.contentPanelClientHeight =
          panelSize.layoutDefaultHeight - panelSize.headerPanelHeight;
        this.style.contentPanelStyle = `height:calc(100% - ${panelSize.headerPanelHeight}px)`;
      } else {
        panelSize.contentPanelClientHeight = panelSize.layoutDefaultHeight;
        this.style.contentPanelStyle = "height:100%";
      }

      if (this.leftPanelEnable) {
        setTimeout(() => {
          this.handleDragLeft();
          window.addEventListener("resize", this.handleDragLeft);
        }, 1000);
        this.style.leftPanelStyle = `max-width:${this.leftPanelMaxWidth}px;min-width:${this.leftPanelMinWidth}px;width:${this.leftPanelMinWidth}px`;
      }

      // Cache right panel height
      panelSize.rightPanelDefaultHeight = panelSize.contentPanelClientHeight;
      panelSize.rightPanelDom = this.$refs.rightPanel;

      if (this.rightTopPanelEnable) {
        // Cache right top panel height
        panelSize.rightTopPanelDefaultHeight = this.$refs.rightTopPanel.offsetHeight;

        this.layoutPanelResize(true);
        window.addEventListener("resize", this.layoutPanelResize);
        this.onScrollResetRightMiddlePanelHeight();
      } else {
        this.$refs.rightMiddlePanel.style.height = "100%";
      }
    },

    layoutPanelResize(init) {
      // Scroll reset
      panelSize.rightPanelDom.scrollTop = 0;
      // Init right top panel placeholder's height, same as right top panel
      this.style.rightTopPanelPlaceholderDynamicHeight = panelSize.rightTopPanelDefaultHeight;

      // Reset right panel height when window resized
      if (init !== true) {
        panelSize.rightPanelDefaultHeight = panelSize.rightPanelDom.offsetHeight;
      }

      // Compute right middle panel's height
      panelSize.rightMiddlePanelDefaultHeight =
        panelSize.rightPanelDefaultHeight - panelSize.rightTopPanelDefaultHeight;
      this.style.rightMiddlePanelDynamicHeight = panelSize.rightMiddlePanelDefaultHeight;
    },

    onScrollResetRightMiddlePanelHeight() {
      this.$refs.rightPanel.onscroll = e => {
        const st = e.currentTarget.scrollTop;

        // Resize right top placeholder height
        if (st >= panelSize.rightTopPanelDefaultHeight) {
          this.style.rightTopPanelPlaceholderDynamicHeight = 0;
        } else {
          this.style.rightTopPanelPlaceholderDynamicHeight =
            panelSize.rightTopPanelDefaultHeight - st;
        }

        // Resize right middle height
        this.style.rightMiddlePanelDynamicHeight =
          panelSize.rightMiddlePanelDefaultHeight +
          (panelSize.rightTopPanelDefaultHeight - this.style.rightTopPanelPlaceholderDynamicHeight);
      };
    },

    handleDragLeft() {
      const self = this;
      document.addEventListener("mousemove", e => {
        if (document.move) {
          const posix = !document.move_target ? { x: 0, y: 0 } : document.move_target.posix;
          const callback = document.call_down || function() {};
          callback.call(document, e, posix);
        }
        e.stopPropagation(); //  阻止事件冒泡
        return false;
      });
      document.addEventListener("mouseup", e => {
        if (document.move) {
          const callback = document.call_up || function() {};
          callback.call(document, e);
          Object.assign(document, {
            move: false,
            move_target: null,
            call_down: false,
            call_up: false
          });
        }

        const left = document.getElementsByClassName("drag-left")[0];
        const right = document.getElementsByClassName("drag-right")[0];
        if (left && right) {
          left.className = left.className.replace("no-select", "");
          right.className = right.className.replace("no-select", "");
        }
        e.stopPropagation();
        document.onselectstart = null;
        return false;
      });

      const $box = document.getElementsByClassName("drag-left")[0];
      if ($box) {
        $box.addEventListener("mousedown", e => {
          const offset = getOffset($box);

          document.posix = { x: e.pageX - offset.left, y: e.pageY - offset.top };

          Object.assign(document, { move: true, move_target: document });
          e.stopPropagation(); //  阻止事件冒泡
          return false;
        });
      }

      const $dragLayout = document.getElementsByClassName("drag-layout-handler")[0];
      if ($dragLayout) {
        $dragLayout.addEventListener("mousedown", e => {
          document.onselectstart = function() {
            return false;
          };
          const posix = {
            w: getWidth($box),
            h: getHeight($box),
            x: e.pageX,
            y: e.pageY
          };

          Object.assign(document, {
            move: true,
            call_down(e) {
              let w = e.pageX - posix.x + posix.w;
              if (w > self.leftPanelMaxWidth) {
                w = self.leftPanelMaxWidth;
              }
              $box.style.width = `${Math.max(self.leftPanelMinWidth, w)}px`;
            }
          });

          const left = document.getElementsByClassName("drag-left")[0];
          const right = document.getElementsByClassName("drag-right")[0];
          if (left && right) {
            left.className += " no-select";
            right.className += " no-select";
          }
          e.stopPropagation(); //  阻止事件冒泡
          return false;
        });
      }
    },

    rightTopPanelChildEventCatcher(e) {
      // Reset right panels default height when right top panel changed height
      if (e.currentTarget.offsetHeight !== panelSize.rightTopPanelDefaultHeight) {
        this.style.rightTopPanelPlaceholderDynamicHeight = e.currentTarget.offsetHeight;
        this.style.rightMiddlePanelDynamicHeight =
          this.style.rightMiddlePanelDynamicHeight +
          panelSize.rightTopPanelDefaultHeight -
          this.style.rightTopPanelPlaceholderDynamicHeight;
        panelSize.rightTopPanelDefaultHeight = this.style.rightTopPanelPlaceholderDynamicHeight;
        panelSize.rightMiddlePanelDefaultHeight = this.style.rightMiddlePanelDynamicHeight;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.tree-list-layout {
  height: 100%;
  overflow: hidden;
  background-color: #fff;
}

.header-panel {
  position: relative;
  background-color: rgb(255 255 255);
  border-bottom: 1px solid rgb(243 243 243);
  box-sizing: border-box;

  .header-shadow {
    position: absolute;
    bottom: 0;
    z-index: 1;
    width: 100%;
    height: 0;
    box-shadow: rgb(0 0 0 / 15%) 0 1px 5px 2px;
  }

  .header-shadow-masklayer {
    position: absolute;
    bottom: 0;
    z-index: 1;
    width: 100%;
    height: 4px;
    background-color: rgb(255 255 255);
    border-bottom: 1px solid rgb(220 220 220);
  }
}

.content-panel {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.draggable-panel-position-wrap {
  position: absolute;
  width: 100%;
  height: 100%;

  /* transition: width .1s;
    transition-timing-function:linear; */
}

.left-panel {
  background-color: #f8f9fa;
  transition: margin-left 0.5s;

  /* transition-timing-function:linear; */
}

.right-panel {
  position: relative;
  overflow: auto;

  .right-top-panel {
    display: flex;
  }

  .right-middle-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .right-top-panel-placeholder {
    background-color: #e8e8e8;
  }
}

.drag-left {
  position: relative;
  background: #f9f9f9;

  .drag-left-layout {
    position: relative;
    height: 100%;
    overflow: auto;
    box-sizing: content-box;
    flex-direction: column;

    .drag-left-title {
      padding-left: 16px;
      line-height: 40px;
    }

    .drag-left-add {
      display: flex;
      padding-left: 13px;
      cursor: pointer;
      align-items: center;

      &:hover {
        background-color: #f2f2f5;
      }

      span {
        height: 40px;
        font-size: 14px;
        font-weight: 400;
        line-height: 40px;
        color: #21252e;
        flex: 1;
      }

      .el-icon-circle-plus {
        position: relative;
        top: 3px;
        font-size: 24px;
        color: $scim-color-primary;
      }
    }
  }

  .drag-layout-handler {
    position: absolute;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    width: 3px;
    height: 100%;
    cursor: ew-resize;
    border-left: 3px solid rgb(0 0 0 / 0%);
    box-sizing: content-box;

    .switch {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      padding: 18px 13px 18px 6px;
      cursor: pointer;

      .off {
        transform: perspective(100px) rotateY(0deg);
      }

      .on {
        transform: perspective(100px) rotateY(180deg);
      }
    }

    .switch::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: -1px;
      background-image: linear-gradient(to right, rgb(249 249 249), rgb(239 239 239));
      border: 1px solid rgb(247 247 247);
      border-left-width: 0;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      content: "";
      transform: perspective(100px) rotateY(51deg);
      box-sizing: unset;
    }

    .switch svg {
      z-index: 1;
    }
  }
}

.drag-right {
  flex: 1;
  width: 0%;
  height: 100%;
  background-color: #fff;
}
</style>
